import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PhaserView, { PhaserRef } from '../Phaser';
import { EventManager } from '../Phaser/engine/events/EventManager';
import { useObjectsApi } from '../../api/objects/objects';
import { TSprite } from '../../models/objects/sprite';
import { Col, message, Row } from 'antd';
import { TObstacle, TScene } from '../../models/objects/objects';
import { IDEPhaserConfig } from './config';
import { getDefaultConfigPhaser } from '../Phaser/types/config';
import { TSound } from '../../models/objects/media';
import { TProject } from '../../models/project/project';
import { useProjectApi } from '../../api/projects/projects';
import { getIDEConfigDefault, IDEConfig, LanguageId } from '../IDE/ide/core/types';
import { UnifiedIDE } from '../IDE/ide';
import ResponsiveIDEPhaserLayout from './ResponsiveIDEPhaserLayout';


interface IDEPhaserProjectProps {
    language: LanguageId;
    project: TProject;
    enableEditCode: boolean;
}

export interface IDEPhaserProjectRef {
    save: () => Promise<void>;
}


const IDEPhaserProject = forwardRef<IDEPhaserProjectRef, IDEPhaserProjectProps>((props, ref) => {

    const [config, setConfig] = useState<IDEPhaserConfig | undefined>()
    const { updateProject } = useProjectApi();
    // const { saveLessonDetails } = useCourseApi();

    const phaserRef = useRef<PhaserRef>(null);
    const eventManagerRef = useRef<EventManager | null>(null);

    const { getSpritesDetails, getScenes, getObstacles, getSounds } = useObjectsApi();

    const [sprites, setSprites] = useState<TSprite[]>([]);
    const [dbScenes, setDbScenes] = useState<TScene[]>([]);
    const [dbObstacles, setDbObstacles] = useState<TObstacle[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [sounds, setSounds] = useState<TSound[]>([]);

    const [latestCode, setLatestCode] = useState<string>('');

    const onWin = () => {
        console.log('🎉 Win condition met!');
        message.success('🎉 Bravo! Vous avez gagné!', 5);
    };

    useEffect(() => {
        // 1) config par défaut
        const defaultMirror = getIDEConfigDefault();

        // 2) config venant du projet (peut être undefined)
        const projectMirror = props.project.config as IDEConfig | undefined;

        // 3) fusion — on veut :
        //    • toutes les valeurs du projet SI elles existent
        //    • sinon celles du défaut
        //    • et TOUJOURS le helpConfig du défaut (ou fusionné si partiel)
        const mergedMirror: IDEConfig = {
            ...defaultMirror,               // valeurs par défaut
            initialCode: props.project.config.code ?? "",
        };

        if (props.project.config.code)
            setLatestCode(props.project.config.code);

        // 4) on alimente le state
        setConfig({
            configMirror: mergedMirror,
            configPhaser: getDefaultConfigPhaser()
        });

        // 5) init EventManager
        eventManagerRef.current = new EventManager();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        setLoading(true);
        Promise.all([getSpritesDetails(), getScenes(), getObstacles(), getSounds()])
            .then((results) => {
                const [charactersData, scenesData, obstaclesData, soundsData] = results;
                setSprites(charactersData.data);
                setDbScenes(scenesData.data);
                setDbObstacles(obstaclesData.data);
                setSounds(soundsData.data);
            })
            .catch((err) => {
                message.error(err.response?.data ?? err.message ?? err);
            })
            .finally(() => {
                setLoading(false);
            });
        // eslint-disable-next-line
    }, []);



    const save = async (): Promise<void> => {
        try {

            let data: TProject = {
                ...props.project,
                config: {
                    code: latestCode,
                }
            };
            await updateProject(props.project.id, data)
        } catch (err: any) {
            message.error(err.response?.data ?? err.message ?? err);
        }
    };

    useImperativeHandle(ref, () => ({
        save,
    }));

    return (
        <div style={{ width: '100%', height: '100%' }} >
            {config && !loading && (
                <ResponsiveIDEPhaserLayout
                    style={{ height: '500px' }}
                    ideComponent={
                        <UnifiedIDE
                            language={props.language}
                            initialCode={props.project.config.code || ''}
                            projectMode={true}
                            adminMode={false}
                            sprites={sprites}
                            scenesConfig={dbScenes}
                            obstacles={dbObstacles}
                            sounds={sounds}
                            bridge={{
                                projectMode: true,
                                // Add other bridge properties as needed
                            } as any}
                            onStop={async () => {
                                await phaserRef.current?.handleResetConfig();
                                await save();
                            }}
                            onCodeChange={(code: string) => setLatestCode(code)}
                            helpGrouped={true}
                            enable={props.enableEditCode}
                        />
                    }
                    phaserComponent={
                        <PhaserView
                            projectMode={true}
                            adminMode={false}
                            ref={phaserRef}
                            eventManager={eventManagerRef.current}
                            config={config.configPhaser}
                            isRunning={true}
                            onWin={onWin}
                            sprites={sprites}
                            scenesConfig={dbScenes}
                            obstacles={dbObstacles}
                            textsConfig={[]}
                            sounds={sounds}
                        />
                    }
                />
            )}
        </div>
    );
});

export default IDEPhaserProject;
